/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import React from 'react'
import Typography from '@material-ui/core/Typography'
import PropTypes from 'prop-types'
import { Image } from '@components/atoms'
import { formattedTimeWithTimezone } from '@utils/dateUtils'
import Link from 'next/link'
import Grid from '@material-ui/core/Grid'
import Chip from '@material-ui/core/Chip'
import Box from '@material-ui/core/Box'
import LaunchIcon from '@material-ui/icons/Launch'
import { centsToDollarsFormatted } from '@lib/conversions'
import { isWeeklyRecurringPayment, isMonthlyRecurringPayment } from '@utils/groupEventUtils'
import { useRouter } from 'next/router'
import { gtmEvent } from '@lib/gtm-utils'
import { DateTime } from 'luxon'
import Avatar from '@material-ui/core/Avatar'
import CoachLogo from '@components/coach-logo'
import useStyles from './group-event-card.styles'

/* eslint-disable camelcase */

/**
 * Creates a display label for the event price: $10 / week / participant + fees ($110 total)
 *
 * @param {Object} params - The parameters for formatting the label.
 * @param {number} params.priceInCents - The price of the event in cents.
 * @param {number} [params.totalPriceInCents] - The total price of the event in cents (optional).
 * @param {string} [params.paymentRecurrenceSchedule] - The payment recurrence schedule (optional).
 * @param {boolean} [params.additionalFees] - Indicates whether additional fees apply (optional).
 * @returns {Array<React.ReactNode>} An array of React elements that make up the formatted label.
 */
export const formattedEventPriceLabel = ({
  priceInCents, totalPriceInCents, paymentRecurrenceSchedule, additionalFees = 0, isJRL, isFG,
}) => {
  const labelPieces = []

  labelPieces.push(
    <span style={{ fontWeight: 'bold' }}>
      {centsToDollarsFormatted(priceInCents + additionalFees)}
    </span>,
  )

  if (isWeeklyRecurringPayment({ paymentRecurrenceSchedule })) {
    labelPieces.push(' / week')
  } else if (isMonthlyRecurringPayment({ paymentRecurrenceSchedule })) {
    labelPieces.push(' / month')
  }

  if (!(isJRL || isFG)) {
    labelPieces.push(' / participant')
  }

  if (isJRL) {
    labelPieces.push(' / player')
  }
  if (isFG) {
    labelPieces.push(' / team')
  }

  if (additionalFees) {
    labelPieces.push(' + 3% processing fee*')
  }

  if (totalPriceInCents && (isWeeklyRecurringPayment({ paymentRecurrenceSchedule }) || isMonthlyRecurringPayment({ paymentRecurrenceSchedule }))) {
    labelPieces.push(` (${centsToDollarsFormatted(totalPriceInCents)} total)`)
  }

  return labelPieces.map((piece, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <React.Fragment key={index}>{piece}</React.Fragment>
  ))
}

function GroupEventCard(props) {
  const {
    slug,
    title,
    price_in_cents,
    total_price_in_cents,
    payment_recurrence_schedule,
    lesson_count,
    additional_fees,
    featured_image_url,
    start_date_time,
    end_date_time,
    coach_facility_timezone,
    coach_facility_state,
    coach_facility_city,
    coach_facility_name,
    coach_name,
    withPrice,
    cardStyle,
    cardHoverEnterHandler,
    cardHoverLeaveHandler,
    isMapInfo,
    squareImage = false,
    max_attendance,
    registration_count,
    registration_full,
    has_waitlist,
    tags,
    source,
    member_type,
    certification_level,
    coach_profile_photo,
    has_plus_subscription,
    __position,
  } = props

  // eslint-disable-next-line react/destructuring-assignment
  const url = props.url || `/things-to-do/events/${slug}`
  const isJRL = tags?.includes('PGA Jr. League')
  const isFG = tags?.includes('PGA Family Cup')

  const router = useRouter()

  const LIMIT_THRESHHOLD = 5
  const spotsRemaining = max_attendance - registration_count
  const hasLimitedSpotsRemaining = spotsRemaining > 0 && spotsRemaining <= LIMIT_THRESHHOLD
  const titleColor = member_type ? '#00234B' : '#00234B99'

  const styles = useStyles({ titleColor, cardStyle })

  const registrationFullLabel = has_waitlist ? 'Waitlist' : 'Event Full'

  const timePeriod = formattedTimeWithTimezone(start_date_time, end_date_time, coach_facility_timezone)
  const startDate = DateTime
    .fromISO(start_date_time, { setZone: true })
    .setZone(coach_facility_timezone || 'UTC')
    .toFormat('yyyy-LL-dd')
  const address = coach_facility_city ? `${coach_facility_city}, ${coach_facility_state}`
    : 'Virtual Facility'

  const cropImage = (originalUrl) => {
    const imageUrl = new URL(originalUrl)
    if (imageUrl.hostname.match(/unsplash.com$/)) {
      if (imageUrl.searchParams.has('fit')) imageUrl.searchParams.set('fit', 'crop')
      imageUrl.searchParams.set('ar', squareImage ? '2:2' : '3:2')

      return imageUrl.toString()
    }
    if (imageUrl.hostname.match(/ctfassets/i)) {
      imageUrl.searchParams.set('w', '700')
      imageUrl.searchParams.set('h', '400')
      imageUrl.searchParams.set('fit', 'fill')

      return imageUrl.toString()
    }
    if (imageUrl.hostname.match(/res\.cloudinary\.com/i)) {
      const [part1, part2] = originalUrl.split('upload')

      return `${part1}upload/w_700${part2}`
    }

    return originalUrl
  }

  const handleClick = (e) => {
    e.stopPropagation()
    e.preventDefault()

    gtmEvent({
      event: 'click-view-event',
      attributes: {
        title,
        start_date: startDate,
        number_of_sessions: lesson_count,
        facility: coach_facility_name,
        location: `${coach_facility_city}, ${coach_facility_state}`,
        price: price_in_cents / 100,
        source,
        category: tags,
        member_type,
        certification_level,
        has_plus_subscription,
        event_list_position: __position,
      },
    })

    router.push(url)
  }

  return (
    <Link
      href={url}
      onClick={handleClick}
      onMouseOver={cardHoverEnterHandler}
      onMouseLeave={cardHoverLeaveHandler}
      className={styles.cardLink}
      data-testid="group-event-card"
    >
      <Grid
        container
        direction={cardStyle || 'column'}
        alignItems="flex-start"
      >
        <Grid
          item
          className={styles.imageContainer}
          xs={cardStyle === 'row' ? 4 : 12}
        >
          {hasLimitedSpotsRemaining && (
            <Chip label="Space Limited" className={styles.chip} />
          )}
          {registration_full && (
            <Chip label={registrationFullLabel} className={styles.chip} />
          )}
          <Box
            className={`${styles.photoWrapper} ${squareImage && styles.squarePhotoWrapper}`}
            my={1}
          >
            <Image
              src={featured_image_url && cropImage(featured_image_url)}
              alt={title}
              className={styles.photo}
            />
          </Box>
        </Grid>
        <Grid
          item
          xs={cardStyle === 'row' ? 8 : 12}
          className={cardStyle === 'row' ? styles.horizontalCard : styles.verticalCard}
        >
          <Grid container>
            <Grid item xs={11}>
              <Typography variant="subtitle1" className={styles.cardTitle}>
                {title}
              </Typography>
              <Typography variant="body1" className={styles.cardSubtitle}>
                {timePeriod}
              </Typography>
              {lesson_count && lesson_count > 1 && (
                <Typography variant="body1" className={styles.cardSubtitle}>
                  {lesson_count} sessions
                </Typography>
              )}
              {isMapInfo ? (
                <>
                  <Typography variant="body1" className={styles.cardSubtitle} gutterBottom>
                    {address}
                  </Typography>
                  <Typography
                    color="secondary"
                    className={styles.cta}
                  >
                      Register
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body1" className={styles.cardSubtitle}>
                    {coach_facility_name}
                  </Typography>
                  <Typography variant="body1" className={styles.cardSubtitle}>
                    {address}
                  </Typography>
                </>
              )}
              {withPrice && (
                <Box pt={1} data-testid="event-price">
                  <Typography variant="body1">
                    {formattedEventPriceLabel({
                      priceInCents: price_in_cents,
                      paymentRecurrenceSchedule: payment_recurrence_schedule,
                      additionalFees: additional_fees,
                      totalPriceInCents: total_price_in_cents,
                      isJRL,
                      isFG,
                    })}
                  </Typography>
                </Box>
              )}
              {member_type && (
                <Box display="flex" alignItems="center" pt={1}>
                  <Avatar
                    src={coach_profile_photo}
                    className={styles.coachAvatar}
                    alt={`${coach_name}'s profile picture`}
                  >
                    {(coach_name || '').substring(0, 1)}
                  </Avatar>
                  <Typography variant="body1">
                    {coach_name}
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={1} className={styles.externalWrapper}>
              {!slug && <LaunchIcon data-testid="launch-icon" />}
              <Box className={styles.logoWrapper}>
                <CoachLogo memberType={member_type} certificationLevel={certification_level} size="40px" />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Link>
  )
}

GroupEventCard.propTypes = {
  slug: PropTypes.string,
  url: PropTypes.string,
  title: PropTypes.string,
  price_in_cents: PropTypes.number,
  additional_fees: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
  ]),
  featured_image_url: PropTypes.string,
  start_date_time: PropTypes.string,
  end_date_time: PropTypes.string,
  coach_facility_state: PropTypes.string,
  coach_facility_city: PropTypes.string,
  coach_facility_name: PropTypes.string,
  coach_facility_timezone: PropTypes.string,
  coach_name: PropTypes.string,
  withPrice: PropTypes.bool,
  cardStyle: PropTypes.string,
  cardHoverEnterHandler: PropTypes.func,
  cardHoverLeaveHandler: PropTypes.func,
  isMapInfo: PropTypes.bool,
  squareImage: PropTypes.bool,
  max_attendance: PropTypes.number,
  registration_count: PropTypes.number,
  total_price_in_cents: PropTypes.number,
  payment_recurrence_schedule: PropTypes.string,
  lesson_count: PropTypes.number,
  registration_full: PropTypes.bool,
  has_waitlist: PropTypes.bool,
  tags: PropTypes.arrayOf(PropTypes.string),
  source: PropTypes.string,
  member_type: PropTypes.string,
  certification_level: PropTypes.string,
  coach_profile_photo: PropTypes.string,
  has_plus_subscription: PropTypes.bool,
  __position: PropTypes.number,
}

export default GroupEventCard
